/**
 * Detect browser EVM wallets for Hyperliquid signing (EIP-712, L1 orders).
 * Includes MetaMask, Rabby, Phantom Ethereum, Coinbase, Brave, Trust, OKX, EIP-6963 discovery.
 */

import { EVM_WALLET_INSTALL_MSG } from './evmWalletMessages';

const WALLET_PRIORITY = ['rabby', 'metamask', 'phantom', 'coinbase', 'brave', 'trust', 'okx'];

const STORAGE_KEY = 'hlPreferredEvmWalletId';

function sortWallets(wallets) {
  return [...wallets].sort((a, b) => {
    const ai = WALLET_PRIORITY.indexOf(a.id.split('-')[0]);
    const bi = WALLET_PRIORITY.indexOf(b.id.split('-')[0]);
    const ar = ai === -1 ? 99 : ai;
    const br = bi === -1 ? 99 : bi;
    if (ar !== br) return ar - br;
    return a.name.localeCompare(b.name);
  });
}

function classifyProvider(provider) {
  if (!provider?.request) return null;
  if (provider.isPhantom) return { id: 'phantom', name: 'Phantom (Ethereum)' };
  if (provider.isRabby) return { id: 'rabby', name: 'Rabby' };
  if (provider.isMetaMask) return { id: 'metamask', name: 'MetaMask' };
  if (provider.isCoinbaseWallet) return { id: 'coinbase', name: 'Coinbase Wallet' };
  if (provider.isBraveWallet) return { id: 'brave', name: 'Brave Wallet' };
  if (provider.isTrust || provider.isTrustWallet) return { id: 'trust', name: 'Trust Wallet' };
  if (provider.isOkxWallet || provider.isOKExWallet) return { id: 'okx', name: 'OKX Wallet' };
  return { id: 'injected', name: 'Browser wallet' };
}

function mergeWalletLists(...lists) {
  const found = [];
  const seenProviders = new WeakSet();
  const seenIds = new Set();

  const addProvider = (provider, overrideId, overrideName) => {
    if (!provider?.request || seenProviders.has(provider)) return;
    seenProviders.add(provider);
    const classified = classifyProvider(provider);
    let id = overrideId || classified?.id || 'injected';
    let name = overrideName || classified?.name || 'Browser wallet';
    if (seenIds.has(id)) {
      id = `${id}-${found.length}`;
    }
    seenIds.add(id);
    found.push({ id, name, provider });
  };

  for (const list of lists) {
    for (const entry of list || []) {
      if (entry?.provider) {
        addProvider(entry.provider, entry.id, entry.name);
      } else {
        addProvider(entry);
      }
    }
  }

  return sortWallets(found);
}

export function getPreferredEvmWalletId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setPreferredEvmWalletId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function pickEvmWallet(wallets, preferredId = getPreferredEvmWalletId()) {
  const list = sortWallets(wallets || []);
  if (!list.length) return null;
  if (preferredId) {
    const match = list.find((w) => w.id === preferredId);
    if (match) return match;
  }
  return list[0];
}

/** Synchronous detection (window.ethereum + window.phantom.ethereum). */
export function detectEvmWallets() {
  if (typeof window === 'undefined') return [];

  const raw = [];

  if (window.phantom?.ethereum) {
    raw.push({
      id: 'phantom',
      name: 'Phantom (Ethereum)',
      provider: window.phantom.ethereum,
    });
  }

  const eth = window.ethereum;
  if (eth) {
    if (Array.isArray(eth.providers)) {
      for (const p of eth.providers) raw.push(p);
    } else {
      raw.push(eth);
    }
  }

  return mergeWalletLists(raw);
}

/** EIP-6963 multi-wallet discovery (MetaMask + Rabby + others without window.ethereum fights). */
export function discoverEvmWallets6963(timeoutMs = 350) {
  if (typeof window === 'undefined') return Promise.resolve([]);

  return new Promise((resolve) => {
    const announced = [];

    const onAnnounce = (event) => {
      const { info, provider } = event.detail || {};
      if (!provider?.request) return;
      const name = info?.name || 'Browser wallet';
      const baseId = (info?.rdns || name).replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 40);
      announced.push({ id: baseId || `eip6963-${announced.length}`, name, provider, rdns: info?.rdns });
    };

    window.addEventListener('eip6963:announceProvider', onAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce);
      resolve(mergeWalletLists(announced));
    }, timeoutMs);
  });
}

/** Merge sync detection + EIP-6963 announcements. */
export async function discoverAllEvmWallets() {
  const sync = detectEvmWallets();
  const e6963 = await discoverEvmWallets6963();
  return mergeWalletLists(sync, e6963);
}

/** Resolve best wallet for signing (includes EIP-6963 discovery). */
export async function pickActiveEvmWallet(preferredId = getPreferredEvmWalletId()) {
  const wallets = await discoverAllEvmWallets();
  return pickEvmWallet(wallets, preferredId);
}

/**
 * Connect and return EVM address(es).
 * @param {object} provider EIP-1193 provider
 * @param {{ forceAccountPicker?: boolean }} [opts]
 *   forceAccountPicker — revoke site permission first so MetaMask shows a fresh
 *   account chooser (needed to link a *second* MetaMask account).
 * @returns {{ address: string, chainId: string|null, accounts: string[] }}
 */
export async function connectEvmWallet(provider, opts = {}) {
  if (!provider?.request) throw new Error('Wallet does not support EIP-1193');
  const force = !!opts.forceAccountPicker;

  if (force) {
    try {
      await provider.request({
        method: 'wallet_revokePermissions',
        params: [{ eth_accounts: {} }],
      });
    } catch {
      // Older MetaMask / other wallets — ignore and continue.
    }
    try {
      await provider.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }],
      });
    } catch (e) {
      const code = e?.code;
      const msg = String(e?.message || '');
      if (code === 4001 || /reject|denied|cancel/i.test(msg)) throw e;
    }
  }

  // Already connected: do not call wallet_requestPermissions. That re-opens
  // MetaMask's "this site might be a scam" screen on every deposit / sign.
  let accounts = await provider.request({ method: 'eth_accounts' }).catch(() => []);
  if (!Array.isArray(accounts) || !accounts.length) {
    accounts = await provider.request({ method: 'eth_requestAccounts' });
  }
  const normalized = (accounts || [])
    .map((a) => String(a || '').toLowerCase())
    .filter((a) => /^0x[a-f0-9]{40}$/.test(a));
  // de-dupe preserve order
  const uniq = [...new Set(normalized)];
  if (!uniq.length) throw new Error('No account returned from wallet');
  const chainId = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  return { address: uniq[0], chainId, accounts: uniq };
}

/**
 * Ask the wallet to sign a short ownership proof for a specific address.
 * User must have that account selected (or choose it) in MetaMask for the signature.
 */
export async function proveEvmAddressOwnership(provider, expectedAddress) {
  if (!provider?.request) throw new Error('Wallet does not support EIP-1193');
  const expected = String(expectedAddress || '').trim().toLowerCase();
  if (!/^0x[a-f0-9]{40}$/.test(expected)) {
    throw new Error('Enter a valid 0x wallet address (40 hex characters).');
  }

  const { verifyMessage, hexlify, toUtf8Bytes } = await import('ethers');
  const message =
    `Omni — link this Hyperliquid trading wallet\n` +
    `Address: ${expected}\n` +
    `Time: ${new Date().toISOString()}`;
  const msgHex = hexlify(toUtf8Bytes(message));

  // Prefer signing as the expected account so MetaMask can switch/prompt for it.
  const signature = await provider.request({
    method: 'personal_sign',
    params: [msgHex, expected],
  });
  const recovered = String(verifyMessage(message, signature) || '').toLowerCase();
  if (recovered !== expected) {
    throw new Error(
      `MetaMask signed with ${recovered.slice(0, 6)}…${recovered.slice(-4)}, not the address you entered. ` +
        `In MetaMask switch to that account and try again.`
    );
  }
  return { address: expected, signature, message };
}

export { EVM_WALLET_INSTALL_MSG };
